//  APIT-656: the PIN reset is gated on the account password, so a single `resetError` now covers
//  two very different failures — a bad password and a bad PIN — that belong under two different
//  fields. This maps the backend error onto the field that caused it.
//
//  The interceptor resolves non-401 responses instead of rejecting (it returns `error.response`),
//  so the saga throws `response.data` and the payload here is the Response envelope: { code,
//  message }. A genuine network throw lands as an Error, hence the `message` fallbacks.

export type SigningPinResetErrors = {
  //  Shown under the password field.
  passwordError: string | null;
  //  Shown under the PIN boxes (format failures from validatePinFormat).
  pinError: string | null;
  //  Throttled by CurrentPasswordVerifier after 5 failed verifications (15-minute lockout).
  //  Retrying cannot help until it expires, so the form disables submit while this is set.
  lockedOut: boolean;
};

export const NO_RESET_ERRORS: SigningPinResetErrors = {
  passwordError: null,
  pinError: null,
  lockedOut: false,
};

/** Pull a human message off whichever error shape we were handed. */
export const signingPinErrorText = (e: any): string | null =>
  e?.data?.message || e?.message || (typeof e === 'string' ? e : null);

//  Matched on the message rather than the HTTP status deliberately: the three password failures
//  do not share a status (CurrentPasswordVerifierImpl throws BAD_REQUEST for a wrong password and
//  for the lockout, but IAM_ERROR — a 500 — when Keycloak is unreachable), while they do share a
//  field. Substring + case-insensitive so a trailing-punctuation tweak on the backend degrades to
//  the generic branch instead of silently mislabelling the field.
const WRONG_PASSWORD = 'current password is incorrect';
const TOO_MANY_ATTEMPTS = 'too many failed attempts';
const VERIFY_UNAVAILABLE = 'unable to verify the current password';
const PIN_FORMAT = 'signing pin must be';

/**
 * Split a reset failure into the per-field messages the reset form renders.
 * Anything unrecognised falls through to the password field, which is where the reset can
 * actually fail now — showing it there beats swallowing it.
 */
export function mapResetError(error: any): SigningPinResetErrors {
  const text = signingPinErrorText(error);
  if (!text) return NO_RESET_ERRORS;

  const normalized = text.toLowerCase();

  if (normalized.includes(TOO_MANY_ATTEMPTS)) {
    return { passwordError: text, pinError: null, lockedOut: true };
  }
  if (normalized.includes(WRONG_PASSWORD) || normalized.includes(VERIFY_UNAVAILABLE)) {
    return { passwordError: text, pinError: null, lockedOut: false };
  }
  if (normalized.includes(PIN_FORMAT)) {
    return { passwordError: null, pinError: text, lockedOut: false };
  }
  return { passwordError: text, pinError: null, lockedOut: false };
}
